import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useFetch } from '../hooks/useFetch.js';
import { PlatformConnect } from './PlatformConnect.jsx';
import { StackPicker } from './StackPicker.jsx';
import { Button, Input, Field, Alert, Badge, SectionTitle } from './ui.jsx';
import { SetupCallLink } from './SetupCall.jsx';

/**
 * Connect the brand's store in one click.
 *
 * Connect → approve on the platform → back here, with Affily's webhooks already
 * registered on the store. Nothing to paste. A store belongs to the brand, so
 * one connected on another program shows up here too, ready to feed this one.
 *
 * The manual way (paste a webhook secret) stays below: in plain sight for a
 * platform with no one-click on this server, and folded away as a fallback
 * for one that has it.
 */
const PLATFORMS = {
  woocommerce: {
    label: 'WooCommerce',
    why: 'Approve Affily on your store and the orders your creators bring are credited — no plugin, no code.',
    ask: {
      field: 'storeUrl',
      label: 'Your store’s address',
      placeholder: 'https://shop.example.com',
      constraint: 'Starts with https:// — the address shoppers visit',
    },
    attribution:
      'Clicks are matched from WooCommerce’s order attribution, and creator codes work as coupons — Affily creates them in your store.',
  },
  shopify: {
    label: 'Shopify',
    why: 'Install Affily on your shop and the orders your creators bring are credited — no webhook to set up.',
    ask: {
      field: 'shop',
      label: 'Your Shopify address',
      placeholder: 'your-shop.myshopify.com',
      constraint: 'The …myshopify.com address — Shopify admin → Settings → Domains',
    },
    attribution:
      'Clicks are matched from the page the shopper landed on, and creator codes work as discount codes — Affily creates them in your shop.',
  },
  stripe: {
    label: 'Stripe',
    why: 'Approve Affily on Stripe and the sales your creators bring are credited, renewals included — no webhook to create.',
    attribution:
      'Creator codes work as Stripe promotion codes, which Affily creates for you. To credit clicks too, pass the visitor’s aff_click into the Checkout Session as client_reference_id or metadata.aff_click — the one line of code Stripe still needs. Renewals need nothing.',
  },
};

const RETURN_MESSAGES = {
  woocommerce: 'WooCommerce approved Affily.',
  shopify: 'Affily is installed on your Shopify shop.',
  stripe: 'Stripe approved Affily.',
};

const when = (d) => (d ? new Date(d).toLocaleString() : null);

export function StoreConnect({ programId, platforms, stacks = [], onChanged }) {
  const list = useFetch('/connections');
  const [params] = useSearchParams();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const available = list.data?.available ?? {};
  const oneClick = Object.keys(PLATFORMS).filter((key) => available[key]);
  const connections = list.data?.connections ?? [];
  // The platforms that also have a paste-a-secret way in (PlatformConnect).
  const manualKeys = ['stripe', 'shopify'];
  const manualOnly = manualKeys.filter((key) => !available[key]);
  const manualFallback = manualKeys.filter((key) => available[key]);

  const returned = params.get('connected');
  // WooCommerce says success=0 when the brand declined; our own callbacks say error=….
  const declined = params.get('success') === '0';
  const returnError = params.get('error');

  async function changed(message = '') {
    setNotice(message);
    await list.reload();
    onChanged?.();
  }

  // What already feeds this program. Once something does, tracking is done:
  // say so, and fold the other ways in away instead of asking again.
  const liveHere = connections.filter(
    (c) => c.status === 'active' && c.programIds.includes(programId),
  );
  const manualLive = manualKeys.filter((key) => platforms?.[key]?.connected);
  const tracked = liveHere.length > 0 || manualLive.length > 0;
  const trackedBy = [
    ...liveHere.map(
      (c) =>
        `${c.externalId.replace(/^https?:\/\//, '')} (${PLATFORMS[c.platform]?.label ?? c.platform})`,
    ),
    ...manualLive.map((key) => PLATFORMS[key].label),
  ];

  const cards = (keys) =>
    keys.map((key) => (
      <StoreCard
        key={key}
        platformKey={key}
        meta={PLATFORMS[key]}
        programId={programId}
        connections={connections.filter((c) => c.platform === key)}
        onChanged={changed}
        onError={setError}
      />
    ));
  const connectedKeys = oneClick.filter((key) => liveHere.some((c) => c.platform === key));
  const otherKeys = oneClick.filter((key) => !connectedKeys.includes(key));

  const returnAlert = returned && PLATFORMS[returned] && (
    <Alert tone={returnError ? 'negative' : declined ? 'caution' : 'positive'}>
      {returnError
        ? `${PLATFORMS[returned].label} wasn’t connected: ${returnError}`
        : declined
          ? `${PLATFORMS[returned].label} wasn’t approved, so nothing was connected.`
          : `${RETURN_MESSAGES[returned]} Its status is below.`}
    </Alert>
  );

  if (tracked) {
    return (
      <section className="space-y-4">
        <SectionTitle hint="Nothing to install">Tracking is on</SectionTitle>
        {returnAlert}
        <Alert tone="positive">
          The orders your creators bring through {trackedBy.join(' and ')} are credited to this
          program. There is no code to add.
        </Alert>
        <Alert>{error}</Alert>
        <Alert tone="info">{notice}</Alert>

        <div className="space-y-3">{cards(connectedKeys)}</div>
        {manualLive.length > 0 && (
          <PlatformConnect
            programId={programId}
            platforms={platforms}
            onChanged={onChanged}
            only={manualLive}
            title={false}
          />
        )}

        {otherKeys.length > 0 && (
          <details className="rounded-lg border border-rule p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Connect another store
              <span className="ml-2 text-xs font-normal text-ink-muted">
                If you also sell somewhere else
              </span>
            </summary>
            <div className="mt-4 space-y-3">{cards(otherKeys)}</div>
          </details>
        )}

        <ManualSetup
          programId={programId}
          platforms={platforms}
          onChanged={onChanged}
          stacks={stacks}
          webhookKeys={manualKeys.filter((key) => !manualLive.includes(key))}
          hint="For a site your connected store doesn’t cover"
        />
      </section>
    );
  }

  return (
    <section className="space-y-4">
      {oneClick.length > 0 && (
        <>
          <SectionTitle hint="No code, and can't be blocked — the best option if you sell on one">
            Connect your store
          </SectionTitle>
          {returnAlert}
          <Alert>{error}</Alert>
          <Alert tone="info">{notice}</Alert>
          <div className="space-y-3">{cards(oneClick)}</div>
        </>
      )}

      {/* Stripe and Shopify without one-click on this server (its app keys
          aren't set) keep their paste-a-secret card in plain sight: that is
          the only way to connect them, so it must never look missing. */}
      <PlatformConnect
        programId={programId}
        platforms={platforms}
        onChanged={onChanged}
        only={manualOnly}
        title={oneClick.length === 0}
      />

      {/* Everything that isn't one click — a pasted webhook secret, or code —
          folded into one choice, so nothing is offered twice. */}
      <ManualSetup
        programId={programId}
        platforms={platforms}
        onChanged={onChanged}
        stacks={stacks}
        webhookKeys={manualFallback}
        hint="Can’t connect? Paste a webhook secret, or add a few lines of code"
      />
    </section>
  );
}

/**
 * The manual ways in, behind one folded choice: pick how the site is built,
 * and only then see that one's steps. A webhook secret for Shopify or Stripe
 * sits alongside the code stacks rather than in a section of its own.
 */
function ManualSetup({ programId, platforms, onChanged, stacks, webhookKeys, hint }) {
  const [choice, setChoice] = useState(null);
  const options = [
    ...(webhookKeys.length > 0
      ? [{ id: 'webhook', label: `${webhookKeys.map((k) => PLATFORMS[k].label).join(' or ')} webhook` }]
      : []),
    ...stacks.map((s) => ({ id: s.id, label: s.label })),
  ];
  if (options.length === 0) return null;
  const stack = stacks.find((s) => s.id === choice);

  return (
    <details className="rounded-lg border border-rule p-4">
      <summary className="cursor-pointer text-sm font-medium">
        Set up manually
        <span className="ml-2 text-xs font-normal text-ink-muted">{hint}</span>
      </summary>
      <div className="mt-4">
        <SetupCallLink topic="connecting my site to Affily" className="mb-3" />
        <p className="mb-2 text-sm text-ink-muted">How is your site built?</p>
        <div className="flex flex-wrap gap-1.5">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setChoice(o.id)}
              aria-pressed={o.id === choice}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                o.id === choice
                  ? 'border-accent bg-accent text-white'
                  : 'border-rule-strong bg-paper-raised text-ink-muted hover:text-ink'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {choice === 'webhook' && (
          <div className="mt-4">
            <PlatformConnect
              programId={programId}
              platforms={platforms}
              onChanged={onChanged}
              only={webhookKeys}
              title={false}
            />
          </div>
        )}
        {/* Keyed so switching stacks starts the picker on the new one. */}
        {stack && <StackPicker key={stack.id} stacks={[stack]} bare />}
      </div>
    </details>
  );
}

function StoreCard({ platformKey, meta, programId, connections, onChanged, onError }) {
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);

  const here = connections.filter((c) => c.programIds.includes(programId));
  const elsewhere = connections.filter((c) => !c.programIds.includes(programId));

  async function start(e) {
    e?.preventDefault();
    setBusy(true);
    onError('');
    try {
      const { redirectUrl } = await api.post(`/connections/${platformKey}/start`, {
        programId,
        ...(meta.ask ? { [meta.ask.field]: address } : {}),
      });
      // Off to the platform to approve; it sends the brand back to this page.
      window.location.assign(redirectUrl);
    } catch (err) {
      onError(err.message);
      setBusy(false);
    }
  }

  async function run(action, message) {
    setBusy(true);
    onError('');
    try {
      const res = await action();
      await onChanged([message, ...(res?.warnings ?? [])].filter(Boolean).join(' '));
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const disconnect = (c) =>
    run(() => api.delete(`/connections/${c.id}`), `${c.externalId} is disconnected.`);
  const attach = (c, attached) =>
    run(
      () => api.post(`/connections/${c.id}/programs`, { programId, attached }),
      attached ? '' : `${c.externalId} no longer feeds this program.`,
    );

  return (
    <div
      className={`rounded-lg border p-4 ${
        here.some((c) => c.status === 'active')
          ? 'border-positive/25 bg-positive-soft'
          : 'border-rule bg-paper-raised'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-xl">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{meta.label}</h3>
            {here.length === 0 && <Badge tone="accent">One click</Badge>}
          </div>
          <p className="mt-1 text-sm text-ink-muted">{meta.why}</p>
        </div>
        {here.length === 0 && !open && (
          <Button size="sm" onClick={() => (meta.ask ? setOpen(true) : start())} disabled={busy}>
            Connect {meta.label}
          </Button>
        )}
      </div>

      {here.map((c) => (
        <div key={c.id} className="mt-3 border-t border-rule pt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <code className="rounded bg-paper-sunken px-1.5 py-0.5 text-xs">{c.externalId}</code>
              {c.status === 'active' ? (
                <Badge tone="positive">Connected</Badge>
              ) : (
                <Badge tone="negative">Needs attention</Badge>
              )}
              <span className="text-xs text-ink-muted">
                {c.lastEventAt ? `Last order event ${when(c.lastEventAt)}` : 'No orders yet'}
              </span>
            </div>
            <div className="flex gap-2">
              {c.status === 'error' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAddress(c.externalId);
                    setOpen(true);
                  }}
                  disabled={busy}
                >
                  Reconnect
                </Button>
              )}
              {c.programIds.length > 1 && (
                <Button size="sm" variant="ghost" onClick={() => attach(c, false)} disabled={busy}>
                  Stop using for this program
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => disconnect(c)} disabled={busy}>
                Disconnect
              </Button>
            </div>
          </div>
          {c.lastError && <Alert className="mt-2">{c.lastError}</Alert>}
          {c.programIds.length > 1 && (
            <p className="mt-2 text-xs text-ink-muted">
              Also feeds{' '}
              {c.programs
                .filter((p) => p.id !== programId)
                .map((p) => p.title ?? 'another program')
                .join(', ')}
              . Each order goes to the one program its click or creator code belongs to.
            </p>
          )}
          <p className="mt-2 text-xs text-ink-muted">{meta.attribution}</p>
        </div>
      ))}

      {here.length === 0 &&
        elsewhere.map((c) => (
          <div
            key={c.id}
            className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-rule pt-3 text-sm"
          >
            <span>
              <code className="rounded bg-paper-sunken px-1.5 py-0.5 text-xs">{c.externalId}</code>{' '}
              <span className="text-ink-muted">is connected for another program.</span>
            </span>
            <Button size="sm" variant="outline" onClick={() => attach(c, true)} disabled={busy}>
              Use it for this program too
            </Button>
          </div>
        ))}

      {open && meta.ask && (
        <form onSubmit={start} className="mt-4 space-y-3 border-t border-rule pt-4">
          <Field
            label={meta.ask.label}
            required
            constraint={meta.ask.constraint}
            hint="You’ll be sent there to approve Affily, then brought straight back."
          >
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={meta.ask.placeholder}
              required
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || address.trim().length < 4}>
              {busy ? 'Opening…' : `Continue to ${meta.label}`}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
